import { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { entitiesSelectors, fetchTeachers } from '@/store/reducers/entities';
import { TeacherReportItem } from '@/school-management/types/response';

interface UseTeachersResult {
  teachers: TeacherReportItem[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useTeachers(organizationIdOverride?: string): UseTeachersResult {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const teachersState = useAppSelector(entitiesSelectors.teachers);

  const organizationId = useMemo(() => {
    if (organizationIdOverride) return organizationIdOverride;
    return user?.metadata?.organizationDetails?.organizationId;
  }, [organizationIdOverride, user?.metadata?.organizationDetails?.organizationId]);

  useEffect(() => {
    if (!organizationId) return;

    const shouldFetch =
      !teachersState.lastFetched ||
      teachersState.organizationId !== organizationId ||
      teachersState.error !== null;

    if (shouldFetch && !teachersState.isLoading) {
      dispatch(fetchTeachers(organizationId));
    }
  }, [
    organizationId,
    dispatch,
    teachersState.lastFetched,
    teachersState.organizationId,
    teachersState.error,
    teachersState.isLoading,
  ]);

  const reload = async () => {
    if (!organizationId) return;
    await dispatch(fetchTeachers(organizationId));
  };

  return {
    teachers: teachersState.items,
    isLoading: teachersState.isLoading,
    error: teachersState.error,
    reload,
  };
}
